import React, { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import {
  HelpCircle,
  MessageSquare,
  Plus,
  Filter,
  Search,
  ChevronRight,
} from "lucide-react";
import QAPostForm from "./QAPostForm";
import QACard from "./QACard";
import QAThread from "./QAThread";

const CATEGORIES = [
  "All",
  "Teachers",
  "Academic",
  "Registration",
  "General",
  "Hostel",
  "Other",
];
const CAMPUSES = [
  { name: "All Campuses", code: "All" },
  { name: "Islamabad", code: "CUI-ISB" },
  { name: "Abbottabad", code: "CUI-ATD" },
  { name: "Lahore", code: "CUI-LHR" },
  { name: "Wah", code: "CUI-WAH" },
  { name: "Sahiwal", code: "CUI-SWL" },
  { name: "Attock", code: "CUI-ATK" },
];

const QAContainer = ({ userAlias, userId, userCampus, onViewProfile }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedCampus, setSelectedCampus] = useState(userCampus || "All"); // Default to user's campus
  const [showPostForm, setShowPostForm] = useState(false);
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const COMMUNITY_ID = 2; // Campus Q&A

  useEffect(() => {
    fetchQuestions();

    const channel = supabase
      .channel("public:qa_posts")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "posts",
          filter: `community_id=eq.${COMMUNITY_ID}`,
        },
        (payload) => {
          // If in filtered state, only add if it matches selected campus
          if (
            selectedCampus === "All" ||
            payload.new.campus_code === selectedCampus
          ) {
            setQuestions((prev) => [payload.new, ...prev]);
          }
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [selectedCampus]); // Re-subscribe if filter changes to ensure real-time matches UI

  const fetchQuestions = async () => {
    setLoading(true);
    let query = supabase
      .from("posts")
      .select("*, profiles:author_id(avatar_url)")
      .eq("community_id", COMMUNITY_ID)
      .order("created_at", { ascending: false });

    if (selectedCampus !== "All") {
      query = query.eq("campus_code", selectedCampus);
    }

    const { data, error } = await query;
    if (!error) setQuestions(data || []);
    setLoading(false);
  };

  const filteredQuestions = questions.filter((q) => {
    const matchesCategory =
      selectedCategory === "All" || q.category === selectedCategory;
    const matchesSearch = q.content
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">
      {/* Header Section - Premium & Clean */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              Q&A
            </h1>
            <p className="text-xs font-bold text-slate-400 dark:text-slate-400 uppercase tracking-widest">
              {userCampus}
            </p>
          </div>
          <button
            onClick={() => setShowPostForm(true)}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-2xl font-black text-xs flex items-center gap-2 hover:shadow-lg hover:shadow-indigo-500/40 transition-all active:scale-95 uppercase tracking-widest shrink-0"
          >
            <Plus size={16} className="sm:w-[18px]" />
            <span className="hidden sm:inline">Ask</span>
          </button>
        </div>

        {/* Search Bar - Elevated */}
        <div className="relative group">
          <div className="absolute inset-y-0 left-0 pl-4 sm:pl-5 flex items-center pointer-events-none">
            <Search
              size={16}
              className="sm:w-[18px] text-slate-400 group-focus-within:text-indigo-500 transition-colors"
            />
          </div>
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-white dark:bg-white/5 dark:backdrop-blur-sm border border-slate-100 dark:border-indigo-500/20 rounded-lg sm:rounded-[1.5rem] py-3 sm:py-5 pl-10 sm:pl-14 pr-4 sm:pr-6 text-sm sm:text-[15px] font-medium focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all shadow-sm group-hover:shadow-md dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
          />
        </div>

        {/* Filters Panel */}
        <div className="space-y-4">
          {/* Campus Selection - Premium Horizontal List */}
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <p className="text-[10px] font-black text-slate-400 dark:text-slate-400 uppercase tracking-widest">
                Campus
              </p>
              <div className="h-px flex-1 bg-slate-100 dark:bg-indigo-500/10 mx-3"></div>
            </div>
            <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar scroll-smooth">
              {CAMPUSES.map((campus) => (
                <button
                  key={campus.code}
                  onClick={() => setSelectedCampus(campus.code)}
                  className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all border text-nowrap ${
                    selectedCampus === campus.code
                      ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-transparent shadow-lg shadow-indigo-500/30"
                      : "bg-white dark:bg-white/5 text-slate-500 dark:text-slate-400 border-slate-100 dark:border-indigo-500/10 hover:border-slate-300 dark:hover:border-indigo-500/20"
                  }`}
                >
                  {campus.name}
                </button>
              ))}
            </div>
          </div>

          {/* Categories */}
          <div className="flex gap-1.5 overflow-x-auto pb-1.5 no-scrollbar scroll-smooth">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-full text-[10px] sm:text-[11px] font-black whitespace-nowrap transition-all uppercase tracking-wider ${
                  selectedCategory === cat
                    ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/30"
                    : "bg-white dark:bg-white/5 text-slate-400 dark:text-slate-400 border border-slate-100 dark:border-indigo-500/10 hover:text-indigo-600 dark:hover:text-indigo-300"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="space-y-6">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin" />
          </div>
        ) : filteredQuestions.length === 0 ? (
          <div className="text-center py-20 glass-card dark:bg-white/5 dark:border-indigo-500/20 dark:backdrop-blur-sm border-dashed">
            <div className="w-20 h-20 rounded-[2rem] bg-slate-50 dark:bg-white/5 flex items-center justify-center mx-auto mb-6">
              <HelpCircle
                size={32}
                className="text-slate-300 dark:text-slate-500"
              />
            </div>
            <h3 className="text-slate-900 dark:text-white font-black uppercase tracking-widest text-xs">
              No active inquiries
            </h3>
            <p className="text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-widest max-w-xs mx-auto mt-2">
              {selectedCampus !== "All"
                ? `Be the first to ask a question in the ${selectedCampus} campus.`
                : "Be the first to ask a question."}
            </p>
          </div>
        ) : (
          filteredQuestions.map((q) => (
            <QACard
              key={q.id}
              question={q}
              onView={() => setActiveQuestionId(q.id)}
              onViewProfile={onViewProfile}
              userId={userId}
              userCampus={userCampus}
            />
          ))
        )}
      </div>

      {/* Modals */}
      {showPostForm && (
        <QAPostForm
          onClose={() => setShowPostForm(false)}
          userId={userId}
          userAlias={userAlias}
          userCampus={userCampus}
        />
      )}

      {activeQuestionId && (
        <QAThread
          questionId={activeQuestionId}
          onClose={() => setActiveQuestionId(null)}
          onViewProfile={onViewProfile}
          currentUser={{
            id: userId,
            alias: userAlias,
            campus_code: userCampus,
          }}
        />
      )}
    </div>
  );
};

export default QAContainer;
